/**
 * Starts an upload as soon as the parent picks the files, instead of at "Save & Next".
 *
 * The cards are chosen on the family screen, but the upload used to run at the end of the whole
 * form — so the parent sat watching a spinner at the last student, waiting for something they
 * had done several screens earlier. Starting it immediately means it is usually finished by the
 * time they get there.
 *
 * The save step then awaits the same promise rather than starting a second upload. Anything that
 * failed in the background is simply retried there, where the parent can be told about it.
 */

const inFlight = new Map();

/**
 * Starts `run()` unless the identical upload is already in flight, and remembers it under `key`.
 * `key` must describe the files, so choosing a different photo starts a new upload.
 */
export function startBackgroundUpload(key, run) {
    if (!key) return null;
    if (inFlight.has(key)) return inFlight.get(key);

    const promise = run();

    // A failure is dealt with by whoever awaits it at save time. Attach a handler now so a
    // rejection nobody has awaited yet is not reported as unhandled, and forget the failure so
    // the save step starts a fresh attempt.
    promise.catch(() => inFlight.delete(key));

    inFlight.set(key, promise);
    return promise;
}

/** The in-flight (or finished) upload for `key`, or null if there is nothing to wait for. */
export const pendingBackgroundUpload = (key) => (key && inFlight.get(key)) || null;

/** Forget an upload so the next attempt starts again — used when a file is replaced or removed. */
export const forgetBackgroundUpload = (key) => inFlight.delete(key);

export const clearBackgroundUploads = () => inFlight.clear();

/**
 * Describes a set of files well enough to notice a change. Two different photos never produce
 * the same key, so a replaced card is always uploaded again.
 */
export const uploadKey = (prefix, files = []) => {
    const parts = files
        .filter(Boolean)
        .map((file) => `${file.name}:${file.size}:${file.lastModified || 0}`);

    if (parts.length === 0) return null;
    return `${prefix}|${parts.join('|')}`;
};
